import type {
  ContactMergeCandidate,
  ContactMergeRecognition,
  ContactMergeTaskDetail,
  ContactMergeTaskItem,
  ContactMergeTaskPage,
} from "../types";

export interface ContactMergeCandidateRaw {
  contact_id: number;
  legacy_id: string;
  school_name?: string | null;
  contact_name: string;
  primary_sales_user_id?: number | null;
  primary_sales_user_name?: string | null;
  position_name?: string | null;
  phone?: string | null;
  phone2?: string | null;
  remark?: string | null;
  college_department_major_id?: string | null;
  depart_major_name?: string | null;
  weixin_code?: string | null;
}

export interface ContactMergeTaskItemRaw {
  id: number;
  task_no: string;
  status: string;
  current_stage: string;
  version: number;
  target_contact_id: number;
  target_contact_name: string;
  source_count: number;
  requested_by: number;
  requested_at: string;
  updated_at: string;
  error_code?: string | null;
  error_summary?: string | null;
}

export interface ContactMergeTaskPageRaw {
  items: ContactMergeTaskItemRaw[];
  total: number;
  page: number;
  page_size: number;
}

export interface ContactMergeTaskDetailRaw {
  id: number;
  task_no: string;
  status: string;
  current_stage: string;
  version: number;
  source_type: string;
  target: ContactMergeCandidateRaw;
  sources: ContactMergeCandidateRaw[];
  field_resolution: {
    choices?: Record<string, number>;
    values?: Record<string, string | null>;
    custom_values?: Record<string, string | null>;
    source_by_field?: Record<string, number | "custom">;
    primary_sales_user_id?: number | null;
  };
  impact_summary?: Record<string, unknown> | null;
  preview_digest?: string | null;
  approved_preview_digest?: string | null;
  allowed_actions: string[];
  error_code?: string | null;
  error_summary?: string | null;
}

export interface ContactMergeRecognitionRaw {
  image_sha256: string;
  school_name?: string | null;
  contact_names: string[];
  phones: string[];
  raw_text?: string | null;
}

/** 映射联系人候选。 */
export function mapContactMergeCandidate(raw: ContactMergeCandidateRaw): ContactMergeCandidate {
  return {
    contactId: raw.contact_id,
    legacyId: raw.legacy_id,
    schoolName: raw.school_name ?? undefined,
    contactName: raw.contact_name,
    primarySalesUserId: raw.primary_sales_user_id ?? undefined,
    primarySalesUserName: raw.primary_sales_user_name ?? undefined,
    positionName: raw.position_name ?? undefined,
    phone: raw.phone ?? undefined,
    phone2: raw.phone2 ?? undefined,
    remark: raw.remark ?? undefined,
    collegeDepartmentMajorId: raw.college_department_major_id ?? undefined,
    departMajorName: raw.depart_major_name ?? undefined,
    weixinCode: raw.weixin_code ?? undefined,
  };
}

/** 映射任务列表。 */
export function mapContactMergeTaskPage(raw: ContactMergeTaskPageRaw): ContactMergeTaskPage {
  return {
    items: raw.items.map(mapContactMergeTaskItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射任务详情。 */
export function mapContactMergeTaskDetail(raw: ContactMergeTaskDetailRaw): ContactMergeTaskDetail {
  return {
    id: raw.id,
    taskNo: raw.task_no,
    status: raw.status,
    currentStage: raw.current_stage,
    version: raw.version,
    sourceType: raw.source_type,
    target: mapContactMergeCandidate(raw.target),
    sources: raw.sources.map(mapContactMergeCandidate),
    fieldResolution: {
      choices: raw.field_resolution.choices,
      values: raw.field_resolution.values,
      customValues: raw.field_resolution.custom_values,
      sourceByField: raw.field_resolution.source_by_field,
      primarySalesUserId: raw.field_resolution.primary_sales_user_id ?? undefined,
    },
    impactSummary: raw.impact_summary ?? undefined,
    previewDigest: raw.preview_digest ?? undefined,
    approvedPreviewDigest: raw.approved_preview_digest ?? undefined,
    allowedActions: raw.allowed_actions,
    errorCode: raw.error_code ?? undefined,
    errorSummary: raw.error_summary ?? undefined,
  };
}

/** 映射截图识别结果。 */
export function mapContactMergeRecognition(raw: ContactMergeRecognitionRaw): ContactMergeRecognition {
  return {
    imageSha256: raw.image_sha256,
    schoolName: raw.school_name ?? undefined,
    contactNames: raw.contact_names,
    phones: raw.phones,
    rawText: raw.raw_text ?? undefined,
  };
}

/** 映射联系人合并任务列表项。 */
function mapContactMergeTaskItem(raw: ContactMergeTaskItemRaw): ContactMergeTaskItem {
  return {
    id: raw.id,
    taskNo: raw.task_no,
    status: raw.status,
    currentStage: raw.current_stage,
    version: raw.version,
    targetContactId: raw.target_contact_id,
    targetContactName: raw.target_contact_name,
    sourceCount: raw.source_count,
    requestedBy: raw.requested_by,
    requestedAt: raw.requested_at,
    updatedAt: raw.updated_at,
    errorCode: raw.error_code ?? undefined,
    errorSummary: raw.error_summary ?? undefined,
  };
}
